/**
 * Canonical list of merge variables supported by the email campaign sender.
 * Must stay in sync with the recipient `variables` built in server/index.js
 * and the system variables added in server/emailMarketing.js.
 */
export const EMAIL_TEMPLATE_VARIABLES = [
  { name: 'customerName', label: 'Customer Name', example: 'Acme Golf Pro Shop' },
  { name: 'contactName', label: 'Contact Name', example: 'John Smith' },
  { name: 'contactFirstName', label: 'Contact First Name', example: 'John' },
  { name: 'contactLastName', label: 'Contact Last Name', example: 'Smith' },
  { name: 'city', label: 'City', example: 'Los Angeles' },
  { name: 'state', label: 'State', example: 'CA' },
  { name: 'businessType', label: 'Business Type', example: 'Pro Shop' },
  { name: 'currentDate', label: 'Current Date', example: new Date().toLocaleDateString() },
  { name: 'currentYear', label: 'Current Year', example: new Date().getFullYear().toString() },
  { name: 'subject', label: 'Email Subject', example: 'Your Subject Line' }
];

export const SUPPORTED_VARIABLE_NAMES = EMAIL_TEMPLATE_VARIABLES.map(v => v.name);

const VARIABLE_PATTERN = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

/**
 * Extract every {{variable}} name used across the provided content strings.
 * @param {...string} contents
 * @returns {string[]} Unique variable names in order of first appearance
 */
export function extractTemplateVariables(...contents) {
  const found = new Set();
  contents.forEach(content => {
    if (!content || typeof content !== 'string') return;
    VARIABLE_PATTERN.lastIndex = 0;
    let match;
    while ((match = VARIABLE_PATTERN.exec(content)) !== null) {
      found.add(match[1]);
    }
  });
  return Array.from(found);
}

function levenshtein(a, b) {
  const rows = a.length + 1;
  const cols = b.length + 1;
  let prev = Array.from({ length: cols }, (_, i) => i);
  for (let i = 1; i < rows; i++) {
    const curr = [i];
    for (let j = 1; j < cols; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    prev = curr;
  }
  return prev[cols - 1];
}

/**
 * Suggest the closest supported variable name for a likely typo.
 * @param {string} name
 * @returns {string|null} Suggested variable name, or null if nothing is close
 */
export function suggestVariableName(name) {
  const lower = (name || '').toLowerCase();
  let best = null;
  let bestDistance = Infinity;
  SUPPORTED_VARIABLE_NAMES.forEach(candidate => {
    const distance = levenshtein(lower, candidate.toLowerCase());
    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  });
  // Allow more edits for longer names, but never suggest for wildly different words
  const threshold = Math.max(2, Math.floor(lower.length / 3));
  return bestDistance <= threshold ? best : null;
}

/**
 * Find {{variables}} that the sender cannot resolve. Unresolved tags are
 * stripped at send time, so these must be caught before a campaign goes out.
 * @param {...string} contents
 * @returns {Array<{ name: string, suggestion: string|null }>}
 */
export function findUnknownVariables(...contents) {
  return extractTemplateVariables(...contents)
    .filter(name => !SUPPORTED_VARIABLE_NAMES.includes(name))
    .map(name => ({ name, suggestion: suggestVariableName(name) }));
}

/**
 * Build a human-readable error message for unknown variables.
 * @param {Array<{ name: string, suggestion: string|null }>} unknown
 * @returns {string}
 */
export function formatUnknownVariablesMessage(unknown) {
  const details = unknown
    .map(u => (u.suggestion ? `{{${u.name}}} — did you mean {{${u.suggestion}}}?` : `{{${u.name}}}`))
    .join(', ');
  return `Unrecognized variable${unknown.length > 1 ? 's' : ''}: ${details}. ` +
    `Supported variables: ${SUPPORTED_VARIABLE_NAMES.map(n => `{{${n}}}`).join(', ')}.`;
}
